import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import axios from 'axios';

const LoadingContext = createContext();

export const useLoading = () => {
  const context = useContext(LoadingContext);
  if (!context) {
    throw new Error('useLoading must be used within a LoadingProvider');
  }
  return context;
};

export const LoadingProvider = ({ children }) => {
  const [activeRequests, setActiveRequests] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const progressInterval = useRef(null);
  const requestTimers = useRef(new Map());

  // Start progress animation
  const startProgress = () => {
    setProgress(0);
    setIsLoading(true);
    
    // Animate progress from 0 to 90 over time
    let currentProgress = 0;
    progressInterval.current = setInterval(() => {
      currentProgress += Math.random() * 10;
      if (currentProgress > 90) {
        currentProgress = 90;
        clearInterval(progressInterval.current);
      }
      setProgress(currentProgress);
    }, 200);
  };

  // Complete progress animation
  const completeProgress = () => {
    if (progressInterval.current) {
      clearInterval(progressInterval.current);
    }
    setProgress(100);
    
    // Hide after animation completes
    setTimeout(() => {
      setIsLoading(false);
      setProgress(0);
    }, 300);
  };

  useEffect(() => {
    // Request interceptor - increment counter
    const requestInterceptor = axios.interceptors.request.use(
      (config) => {
        // Skip loading indicator for certain requests
        if (config.skipLoading) {
          return config;
        }
        
        // Generate unique ID for this request
        const requestId = Date.now() + Math.random();
        config._requestId = requestId;
        
        // Start a timer - only show loading if request takes > 300ms
        const timerId = setTimeout(() => {
          // Remove from map FIRST to signal timer has fired
          requestTimers.current.delete(requestId);
          // Mark that this request is now being tracked
          config._isTracked = true;
          
          setActiveRequests(prev => {
            const newCount = prev + 1;
            if (newCount === 1) {
              startProgress();
            }
            return newCount;
          });
        }, 300);
        
        requestTimers.current.set(requestId, timerId);
        
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor - decrement counter
    const responseInterceptor = axios.interceptors.response.use(
      (response) => {
        const requestId = response.config._requestId;
        
        // Check if timer is still pending (request completed before 300ms)
        if (requestTimers.current.has(requestId)) {
          // Request completed before timer fired - just cancel the timer
          clearTimeout(requestTimers.current.get(requestId));
          requestTimers.current.delete(requestId);
        } else {
          // Timer already fired, so request was counted - decrement
          setActiveRequests(prev => {
            const newCount = Math.max(0, prev - 1);
            if (newCount === 0) {
              completeProgress();
            }
            return newCount;
          });
        }
        
        return response;
      },
      (error) => {
        const requestId = error.config?._requestId;
        
        if (requestId && requestTimers.current.has(requestId)) {
          // Request failed before timer fired - just cancel the timer
          clearTimeout(requestTimers.current.get(requestId));
          requestTimers.current.delete(requestId);
        } else if (requestId) {
          // Timer already fired, so request was counted - decrement
          setActiveRequests(prev => {
            const newCount = Math.max(0, prev - 1);
            if (newCount === 0) {
              completeProgress();
            }
            return newCount;
          });
        }
        
        return Promise.reject(error);
      }
    );

    // Cleanup interceptors on unmount
    return () => {
      axios.interceptors.request.eject(requestInterceptor);
      axios.interceptors.response.eject(responseInterceptor);
      if (progressInterval.current) {
        clearInterval(progressInterval.current);
      }
      // Clear all pending timers
      requestTimers.current.forEach(timer => clearTimeout(timer));
      requestTimers.current.clear();
    };
  }, []);

  return (
    <LoadingContext.Provider value={{ isLoading, progress, activeRequests }}>
      {children}
    </LoadingContext.Provider>
  );
};

export default LoadingContext;
